/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { SEND_NEGOTIABLE_QUOTE_FOR_REVIEW_MUTATION } from './graphql/SendNegotiableQuoteForReviewMutation';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { transformQuote } from '@/quote-management/data/transforms';

export interface SendForReviewInput {
  quoteUid: string;
  comment?: string;
  attachments?: { key: string }[];
}

export const sendForReview = async (
  input: SendForReviewInput
): Promise<NegotiableQuoteModel | null> => {
  const { quoteUid, comment, attachments } = input;

  if (!quoteUid) {
    throw new Error('Quote UID is required');
  }

  // Build the comment object with attachments if provided
  const commentInput = attachments?.length 
    ? { comment: comment || '', attachments } 
    : comment 
    ? { comment } 
    : null;

  return fetchGraphQl(SEND_NEGOTIABLE_QUOTE_FOR_REVIEW_MUTATION, {
    variables: {
      quoteUid,
      comment: commentInput,
    },
  }).then((response) => {
    const { errors } = response;

    if (errors) {
      // Provide more detailed error information
      const errorMessages = errors.map((error) => error.message).join('; ');
      throw new Error(`Failed to send quote for review: ${errorMessages}`);
    }

    // Transform the quote data
    const quoteData = transformQuote(
      response.data?.sendNegotiableQuoteForReview?.quote
    );

    if (!quoteData) {
      throw new Error(
        'Failed to transform quote data: Invalid response structure'
      );
    }

    // Emit event with transformed quote data on success
    events.emit('quote-management/quote-sent-for-review', {
      quote: quoteData,
      input: { quoteUid, comment, attachments },
    });

    return quoteData;
  });
};

